import {
  faArrowLeftLong,
  faLink,
  faNewspaper,
  faUserCheck,
  faUserFriends,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/button";
import Footer from "../../components/footer";
import Header from "../../components/header";
import Loader from "../../components/loader";
import CustomModal from "../../components/modal";
import FollowerTab from "../../components/publicProfilleTabs/FollowerTab";
import PostsTab from "../../components/publicProfilleTabs/PostsTab";
import Tabs from "../../components/tabs";
import { useTranslate } from "../../context/TranslationContext";
import useCommunityController from "../../controllers/useGuidelineController";
import { useUpdateGuidelineMutation } from "../../redux/Apis/Guideline";
import {
  useCreateGuidelineActionMutation,
  useGetGuidelineActionQuery,
  useReportPostMutation,
  useUpdateGuidelineActionMutation,
} from "../../redux/Apis/GuidelineAction";
import { useGetInstructorsQuery } from "../../redux/Apis/User";
import {
  useCreateUserFollowerMutation,
  useGetFollowersByUserIdQuery,
  useUpdateFollowerMutation,
} from "../../redux/Apis/UserFollower";
import "../instructordetails/style.css";

const LearnerDetails = () => {
  const { id } = useParams();
  const userId = id;
  const navigate = useNavigate();
  const { t } = useTranslate("instructorDetails");
  const { user } = useSelector((state) => state.authSlice);
  const lang = useSelector((state) => state.language.lang);
  const followersRef = useRef(null);
  const postRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const isNavigatingBack = useRef(false);
  const [loadingActions, setLoadingActions] = useState({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportPostId, setReportPostId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePostId, setDeletePostId] = useState(null);
  const [localFollowingMap, setLocalFollowingMap] = useState(new Map());

  const TAB_KEYS = {
    POST: 0,
    FOLLOWERS: 1,
  };

  const { data: userActionsData, refetch: refetchActions } =
    useGetGuidelineActionQuery({ params: { lang } }, { skip: !user });
  const [reportPost, { isLoading: isReporting }] = useReportPostMutation();
  const [createGuidelineAction] = useCreateGuidelineActionMutation();
  const [updateGuidelineAction] = useUpdateGuidelineActionMutation();
  const [updateGuideline] = useUpdateGuidelineMutation();

  const { data, isLoading, refetch } = useGetInstructorsQuery(
    { id, params: {} },
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: followersData,
    isLoading: isFollowersLoading,
    isError: isFollowersError,
    refetch: refetchFollowers,
  } = useGetFollowersByUserIdQuery(
    { params: { userid: id } },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    values: {
      data: communityPosts,
      isLoading: isLoadingCommunity,
      isRefetching,
    },
    functions: { setDiscussions },
    refetch: refetchPosts,
  } = useCommunityController("list");

  const { data: myFollowingData, refetch: refetchMyFollowing } =
    useGetFollowersByUserIdQuery(
      { params: { created_by: user?._id } },
      {
        skip: !user?._id,
      },
    );

  const [createUserFollower, { isLoading: isFollowLoading }] =
    useCreateUserFollowerMutation();

  const [updateFollower] = useUpdateFollowerMutation();

  const myFollowingIds = useMemo(() => {
    return new Set(myFollowingData?.data?.map((item) => item.user_id));
  }, [myFollowingData]);

  const userData = data?.data?.user;
  const isOwnProfile = user?._id === id;

  const postCount = useMemo(() => {
    return communityPosts?.length || 0;
  }, [communityPosts]);

  const followersCount = useMemo(() => {
    return followersData?.data?.length || 0;
  }, [followersData]);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (myFollowingData?.data) {
      const map = new Map();
      myFollowingData.data.forEach((item) => {
        map.set(item.user_id, item._id);
      });
      setLocalFollowingMap(map);
    }
  }, [myFollowingData]);

  useEffect(() => {
    if (isNavigatingBack.current) return;
    if (activeTab === TAB_KEYS.FOLLOWERS) {
      refetchFollowers();
    }
  }, [activeTab]);

  useEffect(() => {
    setActiveTab(TAB_KEYS.POST); // reset tab
  }, [id]);

  // Follow handler
  const handleFollow = async (follower) => {
    if (!user?._id) {
      toast.error(t("toast.loginFirst"));
      return;
    }

    const targetUserId = follower._id || userId || follower.created_by;

    const existingFollowerId = localFollowingMap.get(targetUserId);
    const isCurrentlyFollowing = !!existingFollowerId;

    try {
      if (isCurrentlyFollowing) {
        // optimistic
        setLocalFollowingMap((prev) => {
          const newMap = new Map(prev);
          newMap.delete(targetUserId);
          return newMap;
        });

        await updateFollower({
          id: existingFollowerId,
          payload: { follow: false },
          params: {},
        }).unwrap();

        toast.success(t("toast.unfollowSuccess"));
      } else {
        const response = await createUserFollower({
          payload: {
            user: targetUserId,
            created_by: user?._id,
            follow: true,
          },
          params: {},
        }).unwrap();

        const newFollowerId = response?.data?._id;

        // optimistic update with ID
        setLocalFollowingMap((prev) => {
          const newMap = new Map(prev);
          newMap.set(targetUserId, newFollowerId);
          return newMap;
        });

        toast.success(t("toast.followSuccess"));
      }

      refetchMyFollowing();
      if (activeTab === TAB_KEYS.FOLLOWERS) {
        refetchFollowers();
      }
      refetch();
    } catch (error) {
      console.log("error", error?.message, error?.data?.message);
      toast.error(
        error.message || error.data?.message || "Something went wrong",
      );
    }
  };

  // Copy link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("toast.copySuccess"));
    } catch {
      toast.error(t("toast.copyFailed"));
    }
  };

  const handleFollowerClick = (follower) => {
    if (follower.role === "learner") {
      navigate(`/learner/${follower.created_by}`);
    } else {
      navigate(`/instructor/${follower.created_by}`);
    }
  };

  const isLiked = (postId) =>
    userActionsData?.data.some(
      (a) => a.guideline === postId && a.type === "like" && a.isactive,
    ) || false;

  const isSaved = (postId) =>
    userActionsData?.data.some(
      (a) => a.guideline === postId && a.type === "save" && a.isactive,
    ) || false;

  const isAdminOrModerator =
    user?.role === "admin" || user?.role === "moderator";

  const handleToggleAction = async (postId, type) => {
    if (!user) {
      toast.info(t(`toast.loginTo${type === "like" ? "Like" : "Save"}`));
      return;
    }

    const key = `${postId}-${type}`;
    setLoadingActions((prev) => ({ ...prev, [key]: true }));

    setDiscussions((prev) =>
      prev.map((p) => {
        if (p._id !== postId) return p;
        if (type === "like") {
          const liked = isLiked(postId);
          return { ...p, like: liked ? p.like - 1 : p.like + 1 };
        }
        return p;
      }),
    );

    try {
      const existingAction = userActionsData?.data.find(
        (a) => a.guideline === postId && a.type === type,
      );

      if (existingAction) {
        await updateGuidelineAction({
          id: existingAction._id,
          body: { isactive: !existingAction.isactive },
          params: { lang },
        });
      } else {
        const payload = {
          guideline: postId,
          type,
          result: "true",
        };
        await createGuidelineAction({ payload, params: { lang } });
      }
      await refetchActions();
    } catch (err) {
      toast.error(err.message || `Failed to ${type} post`);
      refetchPosts();
    } finally {
      setLoadingActions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleEditPost = (postId) => {
    if (!user) {
      toast.info(t("toast.loginToEdit"));
      return;
    }
    window.location.href = `/community/edit/${postId}`;
  };

  const handleDeletePost = (postId) => {
    if (!user) {
      toast.info(t("toast.loginToDelete"));
      return;
    }
    setDeletePostId(postId);
    setShowDeleteModal(true);
  };

  const confirmDeletePost = async () => {
    if (!deletePostId) return;

    const key = `${deletePostId}-delete`;
    setLoadingActions((prev) => ({ ...prev, [key]: true }));
    setShowDeleteModal(false);

    try {
      await updateGuideline({
        id: deletePostId,
        body: { isactive: false },
        params: { lang },
      }).unwrap();
      toast.success(t("toast.posDeleted"));
      await refetchPosts();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete post");
    } finally {
      setLoadingActions((prev) => ({ ...prev, [key]: false }));
      setDeletePostId(null);
    }
  };

  const handleReportPost = (postId) => {
    if (!user) {
      toast.info(t("toast.loginToReport"));
      return;
    }
    setReportPostId(postId);
    setShowReportModal(true);
  };

  const submitReport = async () => {
    if (!reportReason.trim()) {
      toast.info(t("toast.selectReason"));
      return;
    }

    try {
      const body = {
        guideline: reportPostId,
        reason: reportReason,
        details: reportDetails || "",
      };
      const response = await reportPost({ body, params: { lang } }).unwrap();
      toast.success(response.message || "Report submitted. Thank you!");
      setShowReportModal(false);
      setReportReason("");
      setReportDetails("");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to report post");
    }
  };

  const handlePostCopyLink = (postId) => {
    const postUrl = `${window.location.origin}/community/${postId}`;
    navigator.clipboard
      .writeText(postUrl)
      .then(() => {
        toast.success(t("toast.copySuccess"));
      })
      .catch(() => {
        toast.error(t("toast.copyError"));
      });
  };

  const isAlreadyFollowingUser = localFollowingMap.has(userId);

  if (isLoading) return <Loader loading={true} />;

  return (
    <>
      <Header />

      <div
        className="learner-profile-page min-vh-100"
        style={{ padding: "140px 0 120px 0" }}
      >
        {/* Profile */}
        <div className="container text-center mb-5">
          <img
            src={userData?.picture}
            alt={`${userData?.first_name} ${userData?.last_name}`}
            className="rounded-2"
            style={{ width: 130, height: 130, objectFit: "cover" }}
          />

          <h4 className="fw-bold mt-3">
            {userData?.first_name} {userData?.last_name}
          </h4>

          <p className="text-muted">{userData?.bio}</p>

          <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
            <button
              className="d-flex align-items-center justify-content-center gap-2 site-btn"
              style={{
                cursor: "pointer",
                minWidth: "auto",
                padding: "6px 30px",
              }}
              onClick={() => {
                if (isNavigatingBack.current) return;

                setActiveTab(TAB_KEYS.POST);

                setTimeout(() => {
                  if (isNavigatingBack.current) return;

                  postRef.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                }, 100);
              }}
            >
              <p className="mb-0 fs-3">{postCount}</p>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faNewspaper} />
                <span className="ms-2 text-nowrap">{t("post")}</span>
              </div>
            </button>
            {/* Followers Button */}
            <button
              className="d-flex align-items-center justify-content-center gap-2 site-btn"
              style={{
                cursor: "pointer",
                minWidth: "auto",
                padding: "6px 30px",
              }}
              onClick={() => {
                if (isNavigatingBack.current) return;

                setActiveTab(TAB_KEYS.FOLLOWERS);

                setTimeout(() => {
                  if (isNavigatingBack.current) return;

                  followersRef.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                }, 100);
              }}
            >
              <p className="mb-0 fs-3">{followersCount}</p>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faUserFriends} />
                <span className="ms-1 text-truncate">{t("followers")}</span>
              </div>
            </button>

            {/* Follow Button */}
            {!isOwnProfile && (
              <button
                onClick={!isFollowLoading ? handleFollow : undefined}
                className={`d-flex align-items-center justify-content-center site-btn ${
                  isAlreadyFollowingUser ? "following" : ""
                }`}
                style={{
                  opacity: isFollowLoading ? 0.6 : 1,
                  cursor: isFollowLoading ? "not-allowed" : "pointer",
                  minWidth: "auto",
                }}
              >
                <FontAwesomeIcon
                  icon={isAlreadyFollowingUser ? faUserCheck : faUserPlus}
                  className="me-2"
                />
                {isAlreadyFollowingUser ? t("following") : t("follow")}
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="container px-4">
          <Tabs
            tabs={[{ label: t("post") }, { label: t("followers") }]}
            active={activeTab}
            setActive={setActiveTab}
          />
        </div>

        {/* COURSES TAB */}
        {/* {activeTab === TAB_KEYS.COURSES && (
          <div className="container py-4">
            {purchasedCourses.length > 0 ? (
              <div className="row">
                {purchasedCourses.map((course) => (
                  <div key={course.courseId} className="col-md-6 mb-4">
                    <div
                      className="p-4 rounded"
                      style={{
                        background: "#0e0e18",
                        border: "0px solid rgba(255,255,255,0.05)",
                      }}
                    >
                      <h5 style={{ color: "#fff" }}>{course.title}</h5>

                      <p
                        className="mb-0"
                        style={{
                          color: "#6b7280",
                          textAlign: "right",
                          fontSize: "14px",
                        }}
                      >
                        By: {course?.instructorName || "Unknown Instructor"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted text-center">
                No purchased courses found
              </p>
            )}
          </div>
        )} */}

        {activeTab === TAB_KEYS.POST && (
          <div
            ref={postRef}
            className="community-wrapper container py-4"
            style={{ padding: "0 5%" }}
          >
            <PostsTab
              posts={communityPosts}
              isLoading={isLoadingCommunity}
              isRefetching={isRefetching}
              user={user}
              navigate={navigate}
              loadingActions={loadingActions}
              handleToggleAction={handleToggleAction}
              isLiked={isLiked}
              isSaved={isSaved}
              handleEditPost={handleEditPost}
              handleDeletePost={handleDeletePost}
              handleReportPost={handleReportPost}
              handleCopyLink={handlePostCopyLink}
              isAdminOrModerator={isAdminOrModerator}
              t={t}
            />
          </div>
        )}

        {/* FOLLOWERS TAB (Static) */}
        {activeTab === TAB_KEYS.FOLLOWERS && (
          <div ref={followersRef} className="container py-4">
            <FollowerTab
              followersData={followersData}
              isFollowersLoading={isFollowersLoading}
              isFollowersError={isFollowersError}
              handleFollowerClick={handleFollowerClick}
              handleFollow={handleFollow}
              myFollowingIds={myFollowingIds}
              localFollowingIds={localFollowingMap}
              user={user}
              t={t}
            />
          </div>
        )}
      </div>

      {/* Sticky Bottom Action Bar (Same as Instructor) */}
      <div className="sticky-action-wrapper">
        <div className="sticky-action-bar">
          <button
            className="action-btn"
            onClick={() => {
              isNavigatingBack.current = true;
              navigate(-1);
            }}
          >
            <FontAwesomeIcon icon={faArrowLeftLong} />
            <span>{t("back")}</span>
          </button>

          <button className="action-btn" onClick={() => navigate("/community")}>
            <span>{t("feed")}</span>
          </button>

          <button className="action-btn" onClick={handleCopyLink}>
            <FontAwesomeIcon icon={faLink} />
            <span>{t("copyText")}</span>
          </button>
        </div>
      </div>

      <Modal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-dark">{t("reportPost")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="text-dark">
                {t("reasonForReporting")}
              </Form.Label>
              <Form.Control
                as="select"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
              >
                <option value="">{t("selectReason")}</option>
                <option value="spam">{t("spam")}</option>
                <option value="inappropriatecontent">
                  {t("inappropriateContent")}
                </option>
                <option value="harassment">{t("harassment")}</option>
                <option value="other">{t("other")}</option>
              </Form.Control>
            </Form.Group>

            {reportReason === "other" && (
              <Form.Group>
                <Form.Label className="text-dark">
                  {t("additionalDetails")}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={t("describeIssue")}
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            text={t("cancel")}
            variant="secondary"
            onClick={() => setShowReportModal(false)}
          />
          <Button
            variant="danger"
            onClick={submitReport}
            disabled={isReporting}
            text={isReporting ? t("reporting") : t("submitReport")}
          />
        </Modal.Footer>
      </Modal>
      <CustomModal
        show={showDeleteModal}
        close={() => setShowDeleteModal(false)}
        heading={t("confirmDelete")}
        action={confirmDeletePost}
        loading={loadingActions[`${deletePostId}-delete`]}
      />

      <Footer />
    </>
  );
};

export default LearnerDetails;
